/**
 * Comparison ("DIFF-2") API client — /api/v1/comparisons.
 *
 * Durable comparison of two property-restoration estimates (ESX/PDF),
 * split into AGREED vs DIFFERENT line items for supplement prep by a
 * public adjuster. Mirrors the Firebase auth interceptor pattern used by
 * triageApi.ts.
 */

import axios from 'axios';
import { auth } from './firebase';
import type { ReverseCompiled, ReverseCompileParams } from '../types/reverseCompile';

const BASE_URL =
  import.meta.env.VITE_API_BASE_URL || 'https://est.anterotrail.com/api/v1';

const client = axios.create({
  baseURL: BASE_URL,
});

client.interceptors.request.use(async (config) => {
  const user = auth.currentUser;
  if (user) {
    try {
      const token = await user.getIdToken();
      config.headers.Authorization = `Bearer ${token}`;
    } catch (err) {
      console.error('[comparisonsApi] could not get firebase token', err);
    }
  } else if (
    import.meta.env.DEV &&
    import.meta.env.VITE_DEV_AUTH_BYPASS === 'true'
  ) {
    config.headers.Authorization = 'Bearer dev-bypass-token';
  }
  return config;
});

client.interceptors.response.use(
  (response) => response.data,
  (error) => {
    // PRP-MCP-10: a locked or capped account gets detail {code, message}, not a string.
    const detail = error?.response?.data?.detail;
    const message =
      (detail && typeof detail === 'object' ? detail.message : detail) ||
      error?.response?.data?.message ||
      error?.message ||
      'Comparison request failed';
    // The HTTP status rides on the Error: the Scope panel has to tell a 404
    // (reverse compile not mounted, or no snapshot stored) from every other
    // failure, and the message alone cannot (T-0800).
    const err = new Error(message) as ApiRequestError;
    err.status = error?.response?.status;
    err.code = detail && typeof detail === 'object' ? detail.code : undefined;
    return Promise.reject(err);
  },
);

/** An Error from this client, carrying the HTTP status when there was one. */
export interface ApiRequestError extends Error {
  status?: number;
  /** `comparison_locked` or `comparison_cap` (PRP-MCP-10) on a 402. */
  code?: string;
}

/** `GET /me/comparison-access`: who may compare and how many are left (PRP-MCP-10). */
export interface ComparisonAccess {
  state: 'admin' | 'entitled' | 'free';
  used: number | null;
  cap: number | null;
  remaining: number | null;
  /** The subscribe line (free) or the support line (at the cap). */
  message: string | null;
  subscribe_url: string;
  trial_url: string;
}

// --- Types ---

export type DiffKind =
  | 'SAME'
  | 'QTY_DELTA'
  | 'VARIANT'
  | 'SWAP_CANDIDATE'
  | 'CUSTOM_PRICE_DELTA'
  | 'ADDED'
  | 'REMOVED'
  | 'UNMAPPED'
  | 'EQUIVALENT';

export type Polarity = 'UPGRADE' | 'DOWNGRADE' | 'UNKNOWN';

export type QtyDeltaKind = 'DISCRETE' | 'MEASUREMENT_GROUP' | null;

export interface CanonicalItem {
  cat: string;
  sel: string;
  activity: string | null;
  description: string;
  // Description with the ESX "{V}" placeholder resolved to the surface noun.
  display_description: string;
  // Human wording for the activity glyph ("&" -> "Remove & Replace").
  action_label: string;
  room: string | null;
  qty: number | null;
  qty_variable: string | null;
  unit: string | null;
  unit_price: number | null;
  is_custom_or_bid: boolean;
  source_ref: string | null;
}

export interface DiffEntry {
  key: string;
  kind: DiffKind;
  cat: string | null;
  room: string | null;
  baseline: CanonicalItem | null;
  comparison: CanonicalItem | null;
  match_tier: string | null;
  polarity: Polarity;
  room_moved: boolean;
  same_surface: boolean;
  qty_delta: number | null;
  qty_ratio: number | null;
  qty_delta_kind: QtyDeltaKind;
  unit_price_delta: number | null;
  // For VARIANT/SWAP pairs: "grade" | "size" | "substitution" | "" (B-683).
  variant_axis: string;
  whole_building: WholeBuildingBreakout | null;
  notes: string[];
}

export interface RoomQty {
  room: string;
  qty: number | null;
  unit: string;
}

export interface WholeBuildingBreakout {
  whole_side: string; // "A" | "B"
  whole_room: string;
  whole_qty: number | null;
  distributed_total: number | null;
  unit: string;
  distributed_rooms: RoomQty[];
}

export interface ComparisonSummary {
  agreed: number;
  contested: number;
  added: number;
  removed: number;
  unmapped: number;
  total_items: number;
  total_different: number;
}

export interface MeasurementGroup {
  id: string;
  ratio: number;
  pair_count: number;
  member_labels: string[];
}

export interface RoomSection {
  room: string;
  key: string;
  entries: DiffEntry[];
  agreed: number;
  different: number;
  // Extended-cost totals per side (qty × unit_price), None-safe. INDICATIVE
  // materiality only — never a scope-difference claim for non-bid lines (B-682).
  a_total: number | null;
  b_total: number | null;
}

export interface ThemeEvidence {
  room: string;
  code: string;
  description: string;
  detail: string;
  amount: number | null;
  a_code: string;
  b_code: string;
  a_description: string;
  b_description: string;
  unit: string;
  a_qty: number | null;
  b_qty: number | null;
  axis: string;
}

export interface ThemeGroup {
  axis: string; // grade | size | substitution | ""
  a_code: string;
  b_code: string;
  a_description: string;
  b_description: string;
  descriptions_match: boolean | null;
  unit: string;
  room_count: number;
  item_count: number;
  a_qty_total: number | null;
  b_qty_total: number | null;
  amount: number | null;
  // Systematic-quantity-pattern note (B-660), e.g. "Estimate B consistently ~39%
  // lower across 10 lines — a measurement-basis difference". null when none.
  pattern: string | null;
  ratio: number | null;
  evidence: ThemeEvidence[];
}

export interface Theme {
  kind: string;
  title: string;
  summary: string;
  direction: string;
  item_count: number;
  amount: number | null;
  evidence: ThemeEvidence[];
  groups: ThemeGroup[];
}

export interface NarrativeSection {
  heading: string;
  narrative: string;
  basis: string | null;
}

export interface NarrativeProse {
  overview: string;
  sections: NarrativeSection[];
  disclaimer: string;
}

export interface ComparisonJobStatus {
  job_id: string;
  status: 'queued' | 'running' | 'done' | 'error' | string;
  phase: string | null;
  phase_label: string | null;
  phase_index: number;
  phase_total: number;
  current: number;
  total: number;
  comparison_id: string | null;
  error: string | null;
}

export interface EquivalenceSuggestion {
  from_cat: string;
  from_sel: string;
  from_activity: string;
  from_description: string;
  from_room: string;
  from_key: string;
  to_cat: string;
  to_sel: string;
  to_activity: string;
  to_description: string;
  to_room: string;
  to_key: string;
  similarity: number;
  pair_count: number;
}

export interface ComparisonReport {
  baseline_filename: string;
  comparison_filename: string;
  baseline_format: string;
  comparison_format: string;
  baseline_price_list: string | null;
  comparison_price_list: string | null;
  whole: DiffEntry[];
  agreed: DiffEntry[];
  different: DiffEntry[];
  room_sections: RoomSection[];
  themes: Theme[];
  suggested_equivalences: EquivalenceSuggestion[];
  summary: ComparisonSummary;
  counts: Record<string, number>;
  measurement_groups: MeasurementGroup[];
  warnings: string[];
}

export interface ComparisonListItem {
  id: string;
  name: string | null;
  project_id: string | null;
  baseline_source_ref: string | null;
  comparison_source_ref: string | null;
  created_at: string;
  updated_at: string;
}

export interface ComparisonCreateResponse {
  id: string;
  name: string | null;
  report: ComparisonReport;
}

// --- Reconciliation ("DIFF-2.2") decisions layer ---

export interface UnmappedCodeDecision {
  cat: string;
  sel: string;
  activity: string;
}

export interface EquivalencePair {
  from: { cat: string; sel: string };
  to: { cat: string; sel: string };
}

export interface ComparisonDecisions {
  room_aliases?: Record<string, string>;
  swap_rejects?: string[];
  swap_confirms?: string[];
  unmapped_codes?: Record<string, UnmappedCodeDecision>;
  equivalences?: EquivalencePair[];
}

export interface ComparisonDetail {
  id: string;
  name: string | null;
  project_id: string | null;
  baseline_source_ref: string | null;
  comparison_source_ref: string | null;
  decisions: ComparisonDecisions;
  report: ComparisonReport;
  created_at: string;
  updated_at: string;
}

export const ComparisonsAPI = {
  access(): Promise<ComparisonAccess> {
    return client.get('/me/comparison-access') as unknown as Promise<ComparisonAccess>;
  },

  list(): Promise<ComparisonListItem[]> {
    return client.get('/comparisons') as unknown as Promise<ComparisonListItem[]>;
  },

  get(id: string): Promise<ComparisonDetail> {
    return client.get(`/comparisons/${id}`) as unknown as Promise<ComparisonDetail>;
  },

  delete(id: string): Promise<void> {
    return client.delete(`/comparisons/${id}`) as unknown as Promise<void>;
  },

  createFromUpload(
    baselineFile: File,
    comparisonFile: File,
    name?: string,
    projectId?: string,
  ): Promise<ComparisonCreateResponse> {
    const formData = new FormData();
    formData.append('baseline_file', baselineFile);
    formData.append('comparison_file', comparisonFile);
    if (name) formData.append('name', name);
    if (projectId) formData.append('project_id', projectId);
    return client.post('/comparisons/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }) as unknown as Promise<ComparisonCreateResponse>;
  },

  createFromHashes(
    baselineParseHash: string,
    comparisonParseHash: string,
    name?: string,
    projectId?: string,
  ): Promise<ComparisonCreateResponse> {
    return client.post('/comparisons', {
      baseline_parse_hash: baselineParseHash,
      comparison_parse_hash: comparisonParseHash,
      ...(name ? { name } : {}),
      ...(projectId ? { project_id: projectId } : {}),
    }) as unknown as Promise<ComparisonCreateResponse>;
  },

  patchDecisions(
    id: string,
    decisions: ComparisonDecisions,
  ): Promise<ComparisonDetail> {
    return client.patch(`/comparisons/${id}/decisions`, {
      decisions,
    }) as unknown as Promise<ComparisonDetail>;
  },

  generateNarrative(id: string): Promise<NarrativeProse> {
    return client.post(`/comparisons/${id}/narrative`) as unknown as Promise<NarrativeProse>;
  },

  createJobFromUpload(
    baselineFile: File,
    comparisonFile: File,
    name?: string,
    projectId?: string,
  ): Promise<{ job_id: string }> {
    const formData = new FormData();
    formData.append('baseline_file', baselineFile);
    formData.append('comparison_file', comparisonFile);
    if (name) formData.append('name', name);
    if (projectId) formData.append('project_id', projectId);
    return client.post('/comparisons/jobs/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    }) as unknown as Promise<{ job_id: string }>;
  },

  /**
   * T-0800 — the reverse compile of one side, for the Scope panel.
   *
   * 404 means the route is not mounted (`REVERSE_COMPILE_ENABLED` off on this
   * server) or the row stores no snapshot for that side; both leave nothing to
   * review, and the caller hides the tab.
   */
  getReverseCompile(
    id: string,
    params: ReverseCompileParams = {},
  ): Promise<ReverseCompiled> {
    return client.get(`/comparisons/${id}/reverse-compile`, {
      params,
    }) as unknown as Promise<ReverseCompiled>;
  },

  getJobStatus(jobId: string): Promise<ComparisonJobStatus> {
    return client.get(`/comparisons/jobs/${jobId}`) as unknown as Promise<ComparisonJobStatus>;
  },
};
